import { useCallback, useEffect, useState } from "react";
import { Loader2, WifiOff } from "lucide-react";
import { cachedShop, fetchShop, loadCart, saveCart, type CartLine, type Shop } from "./api";
import { t, useLang } from "./i18n";
import { Menu } from "./Menu";
import { OrderStatus } from "./OrderStatus";
import { Privacy } from "./Privacy";

type Route = { view: "menu" } | { view: "privacy" } | { view: "order"; token: string; justPaid: boolean };

/** `#/order/<token>` shows one order. PayHere sends the customer back with `?order=<token>&paid=1`, which is folded into the same address. */
function readRoute(): Route {
  const query = new URLSearchParams(location.search), returned = query.get("order");
  if (returned && /^[0-9a-f]{40}$/.test(returned)) {
    history.replaceState(null, "", `${location.pathname}#/order/${returned}`);
    return { view: "order", token: returned, justPaid: query.get("paid") === "1" };
  }
  if (location.hash === "#/privacy") return { view: "privacy" };
  const token = /^#\/order\/([0-9a-f]{40})$/.exec(location.hash)?.[1];
  return token ? { view: "order", token, justPaid: false } : { view: "menu" };
}
export const openOrder = (token: string) => { location.hash = `#/order/${token}`; };
export const openMenu = () => { location.hash = ""; };

export function App() {
  useLang();
  const [route, setRoute] = useState<Route>(readRoute);
  // Opens with the menu from the last visit (if any); the fresh one replaces it a moment later.
  const [shop, setShop] = useState<Shop | null>(() => { const saved = cachedShop(); return saved?.offers ? saved : null; });
  const [error, setError] = useState("");
  const [cart, setCartState] = useState<CartLine[]>(loadCart);
  const setCart = useCallback((next: CartLine[]) => { setCartState(next); saveCart(next); }, []);

  const load = useCallback(() => { setError(""); fetchShop().then(setShop).catch((problem: Error) => setError(problem.message)); }, []);
  // With an old menu on screen, a failed refresh is not worth an error page; ordering is checked by the server anyway.
  useEffect(() => {
    load();
    const onHash = () => { setRoute(readRoute()); scrollTo(0, 0); };
    // Opening hours and sold-out dishes change while the app sits in the background.
    const onVisible = () => { if (document.visibilityState === "visible") fetchShop().then(setShop).catch(() => undefined); };
    // While the app is open, sold-out dishes, opening and offers keep themselves current.
    const timer = setInterval(onVisible, 45_000);
    addEventListener("hashchange", onHash);
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(timer); removeEventListener("hashchange", onHash); document.removeEventListener("visibilitychange", onVisible); };
  }, [load]);

  if (route.view === "privacy") return <Privacy phone={shop?.restaurant.phone ?? ""} onForget={() => setCart([])}/>;
  if (route.view === "order") return <OrderStatus key={route.token} token={route.token} justPaid={route.justPaid} shop={shop}/>;
  if (!shop) return <div className="grid min-h-dvh place-items-center p-8 text-center">
    {error ? <div><WifiOff className="mx-auto mb-4 text-mute" size={40}/><h1 className="text-2xl">{t("loadFail")}</h1><p className="mx-auto mt-2 max-w-xs text-mute">{error}</p><button className="btn-primary mx-auto mt-6 max-w-48" onClick={load}>{t("tryAgain")}</button></div>
      : <Loader2 className="animate-spin text-mango" size={34} aria-label="Loading the menu"/>}
  </div>;
  return <Menu shop={shop} cart={cart} setCart={setCart}/>;
}
